import { router } from "@inertiajs/react";
import { AlertTriangle, Mail } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { send_invitation_bizminer_admin_organization_user_path } from "@/routes";
import type {
  BizminerAdminOrganization,
  BizminerUser,
} from "@/types/serializers";

interface Props {
  organization: BizminerAdminOrganization;
  user: BizminerUser;
}

function formatDateTime(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function InviteDialog({ organization, user }: Props) {
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [initialInvitationSentAt, setInitialInvitationSentAt] = useState<
    string | null
  >(null);

  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen) {
      setInitialInvitationSentAt(user.invitation_sent_at ?? null);
      setSending(false);
    }
    setOpen(isOpen);
  };

  const sendInvitation = () => {
    setSending(true);
    router.post(
      send_invitation_bizminer_admin_organization_user_path(
        organization.id,
        user.id,
      ),
      {},
      {
        preserveScroll: true,
        onSuccess: () => setOpen(false),
      },
    );
  };

  const isSsoOrg = organization.sso_config?.sso_enabled ?? false;
  const wasPreviouslyInvited = !!initialInvitationSentAt;

  if (isSsoOrg) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="w-full">
            <Button variant="outline" className="w-full" disabled>
              <Mail className="h-4 w-4 mr-2" />
              Invite User
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent>
          SSO is enabled for this organization. Invitations are not available.
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" className="w-full">
          <Mail className="h-4 w-4 mr-2" />
          Invite User
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Send Invitation Email</DialogTitle>
          <DialogDescription>
            This will send an invitation email to {user.email_address}. The link
            will expire in 48 hours.
          </DialogDescription>
        </DialogHeader>

        {wasPreviouslyInvited && (
          <div className="flex items-start gap-3 p-3 rounded-md bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-400">
            <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" />
            <p className="text-sm">
              User was previously invited on{" "}
              {formatDateTime(initialInvitationSentAt!)}. Send again?
            </p>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button onClick={sendInvitation} disabled={sending}>
            {sending ? "Sending..." : "Send Invitation"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
